import { useState, useEffect } from 'react';
import { Plus, Pencil, Check, Trash2, Package, Users, ArrowRight, AlertTriangle, X } from 'lucide-react';
import { adminApi } from '../../services/api';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  // Per-tracker pricing
  priceOneShot: number;
  priceYearly: number;
  // Pricing options
  allowOneShot: boolean;
  allowYearly: boolean;
  // Per-tracker limits
  usersPerTracker: number;
  geofencesPerTracker: number;
  retentionDays: number;
  // Plan options
  allowWhiteLabel?: boolean;
  allowSms?: boolean;
  allowWhatsapp?: boolean;
  // Features
  features: string[];
  sortOrder: number;
  highlightLabel?: string;
  priceDisplay: 'HT' | 'TTC';
  // Stripe
  stripePriceIdOneShot?: string;
  stripePriceIdYearly?: string;
  stripeTaxRateId?: string;
}

interface PlanSubscription {
  id: string;
  status: string;
  trackerCount: number;
  pricingType: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    status: string;
  };
}

export default function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [planSubscriptions, setPlanSubscriptions] = useState<PlanSubscription[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [migrationTargetId, setMigrationTargetId] = useState<string>('');
  const [migrating, setMigrating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    priceOneShot: 0,
    priceYearly: 0,
    allowOneShot: true,
    allowYearly: true,
    priceDisplay: 'HT' as 'HT' | 'TTC',
    usersPerTracker: 1,
    geofencesPerTracker: 5,
    retentionDays: 30,
    allowWhiteLabel: false,
    allowSms: false,
    allowWhatsapp: false,
    features: [] as string[],
    sortOrder: 0,
    highlightLabel: '',
    stripePriceIdOneShot: '',
    stripePriceIdYearly: '',
    stripeProductId: '',
    stripeTaxRateId: '',
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const res = await adminApi.getPlans();
      const data = res.data?.plans || res.data?.data || res.data;
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminApi.createPlan(formData);
      setShowModal(false);
      resetForm();
      loadPlans();
    } catch (error) {
      console.error('Erreur création plan:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlan) return;
    try {
      await adminApi.updatePlan(editingPlan.id, formData);
      setShowModal(false);
      setEditingPlan(null);
      resetForm();
      loadPlans();
    } catch (error) {
      console.error('Erreur mise à jour plan:', error);
    }
  };

  const openEditModal = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      displayName: plan.displayName,
      description: plan.description || '',
      priceOneShot: plan.priceOneShot || 0,
      priceYearly: plan.priceYearly || 0,
      allowOneShot: plan.allowOneShot ?? true,
      allowYearly: plan.allowYearly ?? true,
      priceDisplay: plan.priceDisplay || 'HT',
      usersPerTracker: plan.usersPerTracker || 1,
      geofencesPerTracker: plan.geofencesPerTracker || 5,
      retentionDays: plan.retentionDays || 30,
      allowWhiteLabel: plan.allowWhiteLabel ?? false,
      allowSms: plan.allowSms ?? false,
      allowWhatsapp: plan.allowWhatsapp ?? false,
      features: plan.features || [],
      sortOrder: plan.sortOrder || 0,
      highlightLabel: plan.highlightLabel || '',
      stripePriceIdOneShot: plan.stripePriceIdOneShot || '',
      stripePriceIdYearly: plan.stripePriceIdYearly || '',
      stripeProductId: '',
      stripeTaxRateId: plan.stripeTaxRateId || '',
    });
    setShowModal(true);
  };

  const openDeleteModal = async (plan: Plan) => {
    setDeletingPlan(plan);
    setDeleteError(null);
    setPlanSubscriptions([]);
    setMigrationTargetId('');
    setShowDeleteModal(true);

    // Load subscriptions for this plan
    setLoadingSubscriptions(true);
    try {
      const res = await adminApi.getPlanSubscriptions(plan.id);
      setPlanSubscriptions(res.data?.data || []);
    } catch (error) {
      console.error('Error loading plan subscriptions:', error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleMigrateAndDelete = async () => {
    if (!deletingPlan || !migrationTargetId) return;

    setMigrating(true);
    setDeleteError(null);

    try {
      // First migrate subscriptions
      await adminApi.migratePlanSubscriptions(deletingPlan.id, migrationTargetId);

      // Then delete the plan
      await adminApi.deletePlan(deletingPlan.id);

      setShowDeleteModal(false);
      setDeletingPlan(null);
      setPlanSubscriptions([]);
      loadPlans();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors de la migration/suppression';
      setDeleteError(message);
    } finally {
      setMigrating(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;
    try {
      await adminApi.deletePlan(deletingPlan.id);
      setShowDeleteModal(false);
      setDeletingPlan(null);
      loadPlans();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors de la suppression';
      setDeleteError(message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      priceOneShot: 0,
      priceYearly: 0,
      allowOneShot: true,
      allowYearly: true,
      priceDisplay: 'HT' as 'HT' | 'TTC',
      usersPerTracker: 1,
      geofencesPerTracker: 5,
      retentionDays: 30,
      allowWhiteLabel: false,
      allowSms: false,
      allowWhatsapp: false,
      features: [],
      sortOrder: 0,
      highlightLabel: '',
      stripePriceIdOneShot: '',
      stripePriceIdYearly: '',
      stripeProductId: '',
      stripeTaxRateId: '',
    });
    setNewFeature('');
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()],
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Plans</h1>
          <p className="text-gray-500">Configurer les plans par tracker (one-shot ou abonnement annuel)</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingPlan(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Nouveau Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative ${
                plan.highlightLabel ? 'ring-2 ring-cyan-500' : ''
              }`}
            >
              {plan.highlightLabel && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-cyan-500 rounded-full text-xs font-medium text-white">
                  {plan.highlightLabel}
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-cyan-500" />
                  <h3 className="text-xl font-bold text-gray-900">{plan.displayName}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(plan)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Pencil className="h-4 w-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(plan)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>

              {plan.description && (
                <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
              )}

              {/* Pricing per tracker */}
              <div className="mb-6 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400 uppercase font-medium">Prix par tracker</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    plan.priceDisplay === 'TTC'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {plan.priceDisplay || 'HT'}
                  </span>
                </div>
                {plan.allowOneShot && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">One-shot</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-gray-900">
                        {plan.priceOneShot}€
                      </span>
                      <span className="text-gray-400 text-sm">/tracker {plan.priceDisplay || 'HT'}</span>
                    </div>
                  </div>
                )}
                {plan.allowYearly && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">Abonnement annuel</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-gray-900">
                        {plan.priceYearly}€
                      </span>
                      <span className="text-gray-400 text-sm">/tracker/an {plan.priceDisplay || 'HT'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Per-tracker limits */}
              <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-400 uppercase font-medium">Limites par tracker</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Utilisateurs</span>
                  <span className="text-gray-900 font-medium">{plan.usersPerTracker}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Geofences</span>
                  <span className="text-gray-900 font-medium">{plan.geofencesPerTracker}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Historique</span>
                  <span className="text-gray-900 font-medium">{plan.retentionDays} jours</span>
                </div>
              </div>

              {/* Plan options */}
              <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-400 uppercase font-medium">Options</p>
                <div className="flex flex-wrap gap-2">
                  {plan.allowSms ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <Check className="h-3 w-3" /> SMS
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-400 rounded-full text-xs">
                      <X className="h-3 w-3" /> SMS
                    </span>
                  )}
                  {plan.allowWhatsapp ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <Check className="h-3 w-3" /> WhatsApp
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-400 rounded-full text-xs">
                      <X className="h-3 w-3" /> WhatsApp
                    </span>
                  )}
                  {plan.allowWhiteLabel ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <Check className="h-3 w-3" /> White-label
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-400 rounded-full text-xs">
                      <X className="h-3 w-3" /> White-label
                    </span>
                  )}
                </div>
              </div>

              {/* Stripe */}
              <div className="space-y-1 mb-4 pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-400 uppercase font-medium">Stripe</p>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${plan.stripePriceIdYearly ? 'bg-green-500' : 'bg-red-400'}`} />
                  <span className="text-gray-500">Annuel</span>
                  <span className="text-gray-400 font-mono text-xs truncate">
                    {plan.stripePriceIdYearly || 'Non configuré'}
                  </span>
                </div>
                {plan.allowOneShot && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full ${plan.stripePriceIdOneShot ? 'bg-green-500' : 'bg-red-400'}`} />
                    <span className="text-gray-500">One-shot</span>
                    <span className="text-gray-400 font-mono text-xs truncate">
                      {plan.stripePriceIdOneShot || 'Non configuré'}
                    </span>
                  </div>
                )}
              </div>

              {/* Features */}
              {plan.features && plan.features.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium mb-2">Fonctionnalités</p>
                  <ul className="space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Aucun plan configuré
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl m-4 my-8">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPlan ? 'Modifier le Plan' : 'Nouveau Plan'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                Prix par tracker - le client choisira la quantité lors de l'achat
              </p>
            </div>
            <form onSubmit={editingPlan ? handleUpdate : handleCreate}>
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Basic info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Nom technique
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''),
                        })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      required
                      disabled={!!editingPlan}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Nom affiché
                    </label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) =>
                        setFormData({ ...formData, displayName: e.target.value })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-500 text-sm mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    rows={2}
                  />
                </div>

                {/* Pricing per tracker */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Prix par tracker</h3>
                    <div className="flex items-center gap-2">
                      <label className="text-gray-500 text-sm">Affichage :</label>
                      <select
                        value={formData.priceDisplay}
                        onChange={(e) =>
                          setFormData({ ...formData, priceDisplay: e.target.value as 'HT' | 'TTC' })
                        }
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      >
                        <option value="HT">HT (Hors Taxes)</option>
                        <option value="TTC">TTC (Toutes Taxes)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="allowOneShot"
                          checked={formData.allowOneShot}
                          onChange={(e) =>
                            setFormData({ ...formData, allowOneShot: e.target.checked })
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor="allowOneShot" className="text-gray-700 text-sm">
                          Activer One-shot
                        </label>
                      </div>
                      <input
                        type="number"
                        value={formData.priceOneShot}
                        onChange={(e) =>
                          setFormData({ ...formData, priceOneShot: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        min={0}
                        step={0.01}
                        disabled={!formData.allowOneShot}
                        placeholder={`Prix one-shot (€ ${formData.priceDisplay})`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="allowYearly"
                          checked={formData.allowYearly}
                          onChange={(e) =>
                            setFormData({ ...formData, allowYearly: e.target.checked })
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor="allowYearly" className="text-gray-700 text-sm">
                          Activer Abonnement annuel
                        </label>
                      </div>
                      <input
                        type="number"
                        value={formData.priceYearly}
                        onChange={(e) =>
                          setFormData({ ...formData, priceYearly: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        min={0}
                        step={0.01}
                        disabled={!formData.allowYearly}
                        placeholder={`Prix annuel (€ ${formData.priceDisplay})`}
                      />
                    </div>
                  </div>
                </div>

                {/* Per-tracker limits */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Utilisateurs/tracker
                    </label>
                    <input
                      type="number"
                      value={formData.usersPerTracker}
                      onChange={(e) =>
                        setFormData({ ...formData, usersPerTracker: parseInt(e.target.value) || 1 })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Geofences/tracker
                    </label>
                    <input
                      type="number"
                      value={formData.geofencesPerTracker}
                      onChange={(e) =>
                        setFormData({ ...formData, geofencesPerTracker: parseInt(e.target.value) || 1 })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Rétention (jours)
                    </label>
                    <input
                      type="number"
                      value={formData.retentionDays}
                      onChange={(e) =>
                        setFormData({ ...formData, retentionDays: parseInt(e.target.value) || 7 })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={1}
                    />
                  </div>
                </div>

                {/* Plan options */}
                <div className="bg-green-50 p-4 rounded-lg space-y-3">
                  <h3 className="font-medium text-gray-900">Options du plan</h3>
                  <p className="text-xs text-gray-500">
                    Activez les canaux et fonctionnalites premium inclus dans ce plan.
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allowSms}
                        onChange={(e) => setFormData({ ...formData, allowSms: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700 text-sm">SMS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allowWhatsapp}
                        onChange={(e) => setFormData({ ...formData, allowWhatsapp: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700 text-sm">WhatsApp</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allowWhiteLabel}
                        onChange={(e) => setFormData({ ...formData, allowWhiteLabel: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700 text-sm">White-label</span>
                    </label>
                  </div>
                </div>

                {/* Display options */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">Ordre d'affichage</label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) =>
                        setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">Badge (ex: "Populaire")</label>
                    <input
                      type="text"
                      value={formData.highlightLabel}
                      onChange={(e) =>
                        setFormData({ ...formData, highlightLabel: e.target.value })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      placeholder="Laisser vide si pas de badge"
                    />
                  </div>
                </div>

                {/* Stripe */}
                <div className="bg-purple-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-medium text-gray-900">Configuration Stripe</h3>
                  <p className="text-xs text-gray-500">
                    Renseignez les Price ID Stripe pour activer le paiement en ligne.
                    Créez d'abord les produits/prix dans votre <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">dashboard Stripe</a>.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-500 text-sm mb-1">
                        Price ID One-shot
                      </label>
                      <input
                        type="text"
                        value={formData.stripePriceIdOneShot}
                        onChange={(e) =>
                          setFormData({ ...formData, stripePriceIdOneShot: e.target.value })
                        }
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono text-sm"
                        placeholder="price_..."
                      />
                    </div>
                    <div>
                      <label className="block text-gray-500 text-sm mb-1">
                        Price ID Annuel
                      </label>
                      <input
                        type="text"
                        value={formData.stripePriceIdYearly}
                        onChange={(e) =>
                          setFormData({ ...formData, stripePriceIdYearly: e.target.value })
                        }
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono text-sm"
                        placeholder="price_..."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Product ID Stripe (optionnel)
                    </label>
                    <input
                      type="text"
                      value={formData.stripeProductId}
                      onChange={(e) =>
                        setFormData({ ...formData, stripeProductId: e.target.value })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono text-sm"
                      placeholder="prod_..."
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-sm mb-1">
                      Tax Rate ID (pour plans HT, ajoute la TVA au checkout)
                    </label>
                    <input
                      type="text"
                      value={formData.stripeTaxRateId}
                      onChange={(e) =>
                        setFormData({ ...formData, stripeTaxRateId: e.target.value })
                      }
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono text-sm"
                      placeholder="txr_..."
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Créez un taux de taxe dans Stripe &gt; Tax rates, puis collez l'ID ici.
                      Utilisé uniquement pour les plans en HT.
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div>
                  <label className="block text-gray-500 text-sm mb-1">Fonctionnalités</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      placeholder="Ajouter une fonctionnalité..."
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                    <button type="button" onClick={addFeature} className="btn-secondary">
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {formData.features.map((feature, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded"
                      >
                        <span className="text-gray-600 text-sm">{feature}</span>
                        <button
                          type="button"
                          onClick={() => removeFeature(i)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPlan(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingPlan ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Supprimer le plan "{deletingPlan.displayName}"
              </h2>

              {loadingSubscriptions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
                </div>
              ) : planSubscriptions.length > 0 ? (
                <div className="space-y-4">
                  {/* Warning about subscriptions */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">
                          Ce plan a {planSubscriptions.length} subscription(s) active(s)
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          Vous devez migrer ces subscriptions vers un autre plan avant de supprimer.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* List of subscriptions */}
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {planSubscriptions.map((sub) => (
                      <div key={sub.id} className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Users className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{sub.tenant.name}</p>
                            <p className="text-xs text-gray-500">
                              {sub.trackerCount} tracker(s) • {sub.status}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          sub.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                          sub.status === 'TRIALING' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {sub.status}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Migration selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Migrer vers le plan :
                    </label>
                    <select
                      value={migrationTargetId}
                      onChange={(e) => setMigrationTargetId(e.target.value)}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    >
                      <option value="">Sélectionner un plan...</option>
                      {plans
                        .filter((p) => p.id !== deletingPlan.id)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.displayName}
                          </option>
                        ))}
                    </select>
                  </div>

                  {deleteError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {deleteError}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingPlan(null);
                        setDeleteError(null);
                        setPlanSubscriptions([]);
                      }}
                      className="btn-secondary"
                      disabled={migrating}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleMigrateAndDelete}
                      disabled={!migrationTargetId || migrating}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {migrating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Migration...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="h-4 w-4" />
                          Migrer et supprimer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    Êtes-vous sûr de vouloir supprimer ce plan ?
                    Cette action est irréversible.
                  </p>
                  {deleteError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {deleteError}
                    </div>
                  )}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingPlan(null);
                        setDeleteError(null);
                      }}
                      className="btn-secondary"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Supprimer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
